import ClaudeLogoIcon from "@/svg/brand/claude-logo-icon.svg?react";
import CohesiveLogoIcon from "@/svg/brand/cohesive-logo-icon.svg?react";
import OpenAILogoIcon from "@/svg/brand/openai-logo-icon.svg?react";
import {
  HARNESS_IDS,
  HarnessCapability,
  HarnessCatalog,
  HarnessId,
} from "@cohesive/api-client";
import type { ComponentType, SVGProps } from "react";
import { offeredHarness } from "./harnesses";

/** The agents a chat can run on, each a harness id: the composer's name for a catalog row. */
export const AGENT_IDS = {
  COHESIVE: "cohesive",
  CLAUDE_CODE: "claude-code",
  CHATGPT: "chatgpt",
} as const;

export type AgentId = (typeof AGENT_IDS)[keyof typeof AGENT_IDS];

/**
 * One agent the composer offers, read off its catalog row. Cohesive carries its tiers and names
 * no model; a subscription agent carries its harness's models and aliases, and is `connected` only
 * once the user holds a credential for it.
 */
export interface AgentCapability
  extends Pick<
    HarnessCapability,
    | "aliases"
    | "default_model_id"
    | "resolved_default_model_id"
    | "resolved_default_effort_id"
    | "model_switch_scope"
    | "tiers"
    | "default_tier_id"
  > {
  id: AgentId;
  label: string;
  /** The catalog row it runs as: Cohesive is `cohesive-claude-code` with the harness runtime on. */
  harness: HarnessId;
  connected: boolean;
  account_email?: string;
  /** The agent a new chat starts on when nothing picks one. */
  default: boolean;
  /** Empty on Cohesive, whose dial is the tier. */
  models: HarnessCapability["models"];
}

interface AgentMeta {
  label: string;
  Logo: ComponentType<SVGProps<SVGSVGElement>>;
  /** The chip under the composer: the agent the chat runs on, by name. */
  chipLabel: string;
  /** The chip while the picked subscription is not connected. */
  notConnectedLabel?: string;
  /** The agent card's second line. */
  cardSubline: string;
  /** The agent menu row's second line. */
  menuSubline: string;
  /** The subscription's name where the user connects it ("Connect Claude"). */
  providerName?: string;
  /** What the user buys to run it. */
  plan?: string;
}

/**
 * Display metadata per agent. Cohesive is never described as Claude Code: it runs claude-code on
 * Cohesive's credential, and its copy names neither.
 */
export const AGENT_META: Record<AgentId, AgentMeta> = {
  [AGENT_IDS.COHESIVE]: {
    label: "Cohesive",
    Logo: CohesiveLogoIcon,
    chipLabel: "Cohesive",
    cardSubline: "Built into Cohesive",
    menuSubline: "Built into Cohesive",
  },
  [AGENT_IDS.CLAUDE_CODE]: {
    label: "Claude Code",
    Logo: ClaudeLogoIcon,
    chipLabel: "Claude",
    notConnectedLabel: "Claude · Not connected",
    cardSubline: "Your subscription",
    menuSubline: "Claude Pro or Max",
    providerName: "Claude",
    plan: "Claude Pro or Max",
  },
  [AGENT_IDS.CHATGPT]: {
    label: "ChatGPT",
    Logo: OpenAILogoIcon,
    chipLabel: "ChatGPT",
    notConnectedLabel: "ChatGPT · Not connected",
    cardSubline: "Your subscription",
    menuSubline: "ChatGPT Plus or Pro",
    providerName: "ChatGPT",
    plan: "ChatGPT Plus or Pro",
  },
};

/** The agents in the order every chooser lists them. */
export const AGENT_ORDER: AgentId[] = [
  AGENT_IDS.COHESIVE,
  AGENT_IDS.CLAUDE_CODE,
  AGENT_IDS.CHATGPT,
];

/** Whether a harness is Cohesive's, on either loop. Sessions that predate the stamp are. */
export function isCohesiveHarness(harness?: string): boolean {
  return (
    !harness ||
    harness === HARNESS_IDS.COHESIVE ||
    harness === HARNESS_IDS.COHESIVE_CLAUDE_CODE
  );
}

/**
 * The agent a stamped harness runs as. Claude Code and ChatGPT are their own harnesses; everything
 * else is stated as Cohesive — its two loops, and a legacy harness that is no agent's (opencode).
 */
export function agentOf(harness?: string): AgentId {
  if (harness === HARNESS_IDS.CLAUDE_CODE) return AGENT_IDS.CLAUDE_CODE;
  if (harness === HARNESS_IDS.CODEX) return AGENT_IDS.CHATGPT;
  return AGENT_IDS.COHESIVE;
}

/** The harness id an agent is stored as where it must follow the flag: Cohesive as `cohesive`. */
export function agentHarnessId(agent: AgentCapability): HarnessId {
  return agent.id === AGENT_IDS.COHESIVE ? HARNESS_IDS.COHESIVE : agent.harness;
}

const AGENT_HARNESS: Record<AgentId, HarnessId> = {
  [AGENT_IDS.COHESIVE]: HARNESS_IDS.COHESIVE,
  [AGENT_IDS.CLAUDE_CODE]: HARNESS_IDS.CLAUDE_CODE,
  [AGENT_IDS.CHATGPT]: HARNESS_IDS.CODEX,
};

/**
 * The agents the catalog offers, in chooser order, each read off the row it runs as. OpenCode and
 * any row no agent claims are left out: the composer never offers them.
 */
export function agentsFromCatalog(catalog: HarnessCatalog): AgentCapability[] {
  const defaultAgent = agentOf(catalog.default_harness);
  const agents: AgentCapability[] = [];
  for (const id of AGENT_ORDER) {
    // Cohesive is whichever row the catalog offers it as (offeredHarness).
    const harness = offeredHarness(
      AGENT_HARNESS[id],
      catalog.harness_capabilities,
    );
    const row = catalog.harness_capabilities.find((c) => c.id === harness);
    if (!row) continue;
    const cohesive = id === AGENT_IDS.COHESIVE;
    agents.push({
      id,
      label: AGENT_META[id].label,
      harness: row.id as HarnessId,
      connected: cohesive || (row.connected ?? row.available),
      account_email: row.account_email,
      default: id === defaultAgent,
      models: cohesive ? [] : row.models,
      aliases: cohesive ? [] : row.aliases,
      default_model_id: cohesive ? undefined : row.default_model_id,
      resolved_default_model_id: cohesive
        ? undefined
        : row.resolved_default_model_id,
      resolved_default_effort_id: cohesive
        ? undefined
        : row.resolved_default_effort_id,
      model_switch_scope: row.model_switch_scope,
      tiers: row.tiers,
      default_tier_id: row.default_tier_id,
    });
  }
  return agents;
}

export function findAgent(
  agents: AgentCapability[] | undefined,
  id: AgentId,
): AgentCapability | undefined {
  return agents?.find((agent) => agent.id === id);
}

/** The provider id a subscription agent's Connect dialog opens on. */
export function agentProvider(id: AgentId): "anthropic" | "openai" | undefined {
  if (id === AGENT_IDS.CLAUDE_CODE) return "anthropic";
  if (id === AGENT_IDS.CHATGPT) return "openai";
  return undefined;
}
